import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preview", "fileInput", "captureButton", "retakeButton", "usePhotoButton", "video", "canvas"]
  static values = {
    maxWidth: { type: Number, default: 1200 },
    maxHeight: { type: Number, default: 1200 },
    quality: { type: Number, default: 0.8 }
  }

  connect() {
    this.stream = null
    this.capturedImage = null

    // Check camera support and update UI
    this.checkCameraSupport()
  }

  checkCameraSupport() {
    if (!this.hasCameraSupport) {
      // Hide camera button and show only file upload
      const cameraButton = this.element.querySelector('[data-action*="startCamera"]')
      if (cameraButton) {
        cameraButton.style.display = 'none'
        cameraButton.disabled = true
      }

      // Add info text about camera not being supported
      const infoDiv = document.createElement('div')
      infoDiv.className = 'alert alert-info'
      infoDiv.innerHTML = 'ℹ️ Camera not available on this device. Please use file upload.'

      const fileInput = this.fileInputTarget.parentNode
      fileInput.insertBefore(infoDiv, this.fileInputTarget)
    }
  }

  disconnect() {
    this.stopCamera()
  }

  async startCamera() {
    try {
      // Hide file input and show camera UI
      this.fileInputTarget.style.display = 'none'
      this.previewTarget.style.display = 'block'

      // Request camera access
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use rear camera if available
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })

      this.videoTarget.srcObject = this.stream
      this.videoTarget.play()

      // Show camera controls
      this.captureButtonTarget.style.display = 'inline-block'
      this.captureButtonTarget.disabled = false

    } catch (error) {
      console.error('Error accessing camera:', error)
      alert('Unable to access camera. Please use file upload instead.')
      this.showFileInput()
    }
  }

  capturePhoto() {
    const video = this.videoTarget
    const canvas = this.canvasTarget
    const context = canvas.getContext('2d')

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert to blob and prepare for upload
    canvas.toBlob((blob) => {
      this.capturedImage = blob
      this.showCapturedPhoto(canvas.toDataURL())
    }, 'image/jpeg', this.qualityValue)
  }

  showCapturedPhoto(dataUrl) {
    // Hide video and show captured image
    this.videoTarget.style.display = 'none'
    this.captureButtonTarget.style.display = 'none'

    // Create preview image
    const img = document.createElement('img')
    img.src = dataUrl
    img.className = 'img-fluid rounded'
    img.style.maxHeight = '300px'

    // Clear preview and add image
    this.previewTarget.innerHTML = ''
    this.previewTarget.appendChild(img)

    // Show action buttons
    this.retakeButtonTarget.style.display = 'inline-block'
    this.usePhotoButtonTarget.style.display = 'inline-block'
  }

  retakePhoto() {
    // Clear captured image
    this.capturedImage = null

    // Show video again
    this.videoTarget.style.display = 'block'
    this.captureButtonTarget.style.display = 'inline-block'

    // Hide action buttons
    this.retakeButtonTarget.style.display = 'none'
    this.usePhotoButtonTarget.style.display = 'none'

    // Clear preview
    this.previewTarget.innerHTML = ''
    this.previewTarget.appendChild(this.videoTarget)
    this.previewTarget.appendChild(this.canvasTarget)
  }

  async usePhoto() {
    if (!this.capturedImage) return

    // Create a File object from the blob
    const file = new File([this.capturedImage], `meal-${Date.now()}.jpg`, {
      type: 'image/jpeg'
    })

    // Create a DataTransfer object and add our file
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)

    // Set the files on the file input
    this.fileInputTarget.files = dataTransfer.files

    // Trigger change event to notify Rails
    this.fileInputTarget.dispatchEvent(new Event('change', { bubbles: true }))

    // Clean up camera
    this.stopCamera()
    this.showFileInput()

    // Show success message
    this.showSuccessMessage()
  }

  showSuccessMessage() {
    const successDiv = document.createElement('div')
    successDiv.className = 'alert alert-success mt-2'
    successDiv.innerHTML = '✅ Photo captured successfully!'

    this.previewTarget.appendChild(successDiv)

    // Remove success message after 3 seconds
    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
  }

  showFileInput() {
    this.fileInputTarget.style.display = 'block'
    this.previewTarget.style.display = 'none'

    // Hide all camera buttons
    this.captureButtonTarget.style.display = 'none'
    this.retakeButtonTarget.style.display = 'none'
    this.usePhotoButtonTarget.style.display = 'none'
  }

  // Check if device supports camera
  get hasCameraSupport() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  }
}